import {
  type LayoutType,
  type MaskOptions,
} from '@wandb/weave/common/types/media';
import {
  type RgbaColor,
  type RgbColor,
} from '@wandb/weave/common/util/render_babylon';
import memoize from 'memoize-one';

import {type RunSignature} from '../../types/run';
import {runHistoryRowsWithOneOfKeys} from '../../util/media';
import {
  type AllBoundingBoxControls,
  type AllMaskControls,
  type MediaPanelCardControl,
  type MoleculeConfig,
  type RunWithHistoryAndMediaWandb,
} from '../MediaCard';

export const rowsWithKeysMemo = memoize(runHistoryRowsWithOneOfKeys);
export type Axis = 'step' | 'index' | 'run' | 'camera';
export type LayoutMode = 'gallery' | 'grid' | 'compare';

export type PointColorSetting = {
  type: 'rgb';
  color: RgbColor;
};

export type KeyTypes = Partial<Record<string, string>>;

export type MediaSyncState = {
  currentTime: number;
  isPlaying: boolean;
  playbackRate: number;
  leaderId: string | null;
  lastUpdate: number;
};

export type ColumnConfig = {
  run?: string;
  step?: number;
  index?: number;
  mediaKey?: string;
};

export type TileControlType = 'run' | 'step' | 'index' | 'mediaKey';
export type TileControlValue = 'linked' | 'individual' | 'hidden';
export type TileControls = Partial<Record<TileControlType, TileControlValue>>;
export type FanOutVariable = 'step' | 'index' | 'run';

export type CompareSettings = {
  columns?: ColumnConfig[];
  runName?: string;
  tileControls?: TileControls;
  fanOutVariable?: FanOutVariable;
};

export type MediaBrowserPanelConfig = {
  chartTitle?: string; //
  stepIndex?: number; // index of the step shown from each run, default: 0
  mediaIndex?: number; // index of the image shown from each run, default: 0
  mediaSliderKey?: string; // key for slider values, defaults to '_step'
  mediaKeys?: string[]; // key for media, like 'examples'

  actualSize?: boolean; //
  fitToDimension?: boolean; //
  videoActualSize?: boolean; // Show videos at their original size (gallery mode); actualSize/fitToDimension are image-only
  pixelated?: boolean; //
  alwaysOriginal?: boolean; // Show original image (no thumbnails) in non-gallery modes

  // Settings for tiling
  columnCount?: number; //

  // Layout mode
  mode?: LayoutMode;

  // mode-specific settings
  gallerySettings?: {
    axis: Axis;
  };
  gridSettings?: {
    xAxis: Axis;
    yAxis: Axis;
  };
  compareSettings?: CompareSettings;

  // min/max values for the axis range selection
  selection?: {
    xAxis?: [number, number];
    yAxis?: [number, number];
  };

  page?: {
    start?: number;
  };

  // How to stack masks
  tileLayout?: {
    [mediaKey: string]: {
      type: LayoutType;
      maskOptions: MaskOptions[];
    };
  };

  // The number of steps to increase on the up and down buttons of the step slider
  stepStrideLength?: number;
  snapToExistingStep?: boolean;
  // `null` means "explicitly show all" — it blocks an inherited limit in the
  // workspace → section → panel cascade, unlike `undefined` which inherits.
  maxGalleryItems?: number | null;
  maxRuns?: number;
  maxYAxisCount?: number;
  moleculeConfig?: MoleculeConfig;
  segmentationMaskConfig?: AllMaskControls;
  boundingBoxConfig?: AllBoundingBoxControls;
  sliderSync?: boolean;

  // Video synchronization settings
  videoPlaybackSyncEnabled?: boolean; // if sync is on at _any_ settings level
  videoPlaybackSync?: boolean; // if sync is on at _this_ settings level
  videoPlaybackSyncAutoplay?: boolean;
  videoPlaybackSyncLoop?: boolean;
  // seek synchronized videos to this time on first load
  videoPlaybackSyncCurrentTime?: number;

  disableVideoStream?: boolean; // Opt out of video streaming

  // Object3D settings
  object3DBackgroundColor?: RgbaColor;
  pointColor?: PointColorSetting;
  useRightHandedSystem?: boolean;

  // How many seconds to seek forward/back when using the arrow keys
  videoPlayerTimeStep?: number;
};

export type NonEmptyTile = {
  mediaKey: string;
  mediaSliderKey?: string;
  mediaSliderKeyValue?: number;
  mediaStep: number;
  mediaIndex: number;
  runSignature: RunSignature;
  run: RunWithHistoryAndMediaWandb;
  controls?: MediaPanelCardControl;
  meta?: {
    camera?: {
      cameraIndex: number;
    };
  };

  // We don't set this for regular tiles, but it make the typing cleaner
  // to interact with empty tiles to have an optional here
  isEmpty?: false;
};
